/** @file commandQueue: the server-confirmed document plus optimistic batches on top (settle, drop, rebase). */
import type { PuppetDoc } from '../state/puppetDoc';
import type { Command } from '@zilo/pose3d-commands/types/command';

/** Applies a batch to a document locally; returns the input document when the batch does not apply. */
export type ApplyFn = (doc: PuppetDoc, commands: Command[]) => PuppetDoc;

/** One optimistic batch: its key (from `push`) and its commands. */
export type QueuedBatch = { key: number; commands: Command[] };

/** The queue of one open puppet; every method returns or keeps the optimistic `view`. */
export type CommandQueue = {
  /** The last document the server confirmed (its `revision` is the server's). */
  confirmed: () => PuppetDoc;
  /** `confirmed` with every pending batch applied, oldest first: what the panels show. */
  view: () => PuppetDoc;
  pending: () => number;
  /** The oldest pending batch (the one in flight), if any. */
  head: () => QueuedBatch | undefined;
  /** Replaces the confirmed document and forgets every pending batch (a load or resync). */
  reset: (doc: PuppetDoc) => void;
  /** Adds an optimistic batch and returns its key. */
  push: (commands: Command[]) => number;
  /** The server committed batch `key` as `revision`: it moves into `confirmed`. A key no longer queued is ignored. */
  settle: (key: number, revision: number) => void;
  /** The server refused batch `key`: it is forgotten, so the view is the pre-batch document. */
  drop: (key: number) => void;
  /** Someone else committed `commands` as `revision`: applied to `confirmed`, pending batches re-applied on top. */
  remote: (commands: Command[], revision: number) => void;
};

/** A queue starting at `doc`; `apply` is the local command engine (`applyLocalBatch` in the store). */
export function commandQueue(doc: PuppetDoc, apply: ApplyFn): CommandQueue {
  let confirmed = doc;
  let batches: QueuedBatch[] = [];
  let view: PuppetDoc | null = doc;
  let nextKey = 1;
  const advance = (commands: Command[], revision: number): void => {
    confirmed = { ...apply(confirmed, commands), revision };
    view = null;
  };
  return {
    confirmed: () => confirmed,
    view: () => (view ??= batches.reduce((d, b) => apply(d, b.commands), confirmed)),
    pending: () => batches.length,
    head: () => batches[0],
    reset: (next) => { confirmed = next; batches = []; view = next; },
    push: (commands) => {
      const key = nextKey++;
      batches.push({ key, commands });
      view = view === null ? null : apply(view, commands);
      return key;
    },
    settle: (key, revision) => {
      const batch = batches.find((b) => b.key === key);
      if (batch === undefined) return;
      batches = batches.filter((b) => b !== batch);
      advance(batch.commands, revision);
    },
    drop: (key) => {
      const before = batches.length;
      batches = batches.filter((b) => b.key !== key);
      if (batches.length !== before) view = null;
    },
    remote: (commands, revision) => { advance(commands, revision); },
  };
}
